'use client';

import { useEffect, useSyncExternalStore } from 'react';

type VisualEditorStoreModule = typeof import('@/stores/visualEditorStore');

let visualEditorStoreModulePromise: Promise<VisualEditorStoreModule> | null = null;
let projectNameSnapshot = '';
let unsubscribeVisualEditorStore: (() => void) | null = null;
const projectNameListeners = new Set<() => void>();
let preparedProjectChatSidebarState: { projectId: string; open: boolean } | null = null;

function publishProjectName(nextProjectName: string) {
  if (projectNameSnapshot === nextProjectName) return;
  projectNameSnapshot = nextProjectName;
  projectNameListeners.forEach((listener) => listener());
}

async function loadVisualEditorStore(): Promise<VisualEditorStoreModule> {
  visualEditorStoreModulePromise ??= import('@/stores/visualEditorStore')
    .then((storeModule) => {
      if (!unsubscribeVisualEditorStore) {
        publishProjectName(storeModule.useVisualEditorStore.getState().projectName);
        unsubscribeVisualEditorStore = storeModule.useVisualEditorStore.subscribe((state) => {
          publishProjectName(state.projectName);
        });
      }
      return storeModule;
    })
    .catch((error) => {
      visualEditorStoreModulePromise = null;
      throw error;
    });

  return visualEditorStoreModulePromise;
}

function subscribeProjectName(listener: () => void) {
  projectNameListeners.add(listener);
  return () => {
    projectNameListeners.delete(listener);
  };
}

function getProjectNameSnapshot() {
  return projectNameSnapshot;
}

export function useVisualEditorProjectName(enabled: boolean): string {
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    loadVisualEditorStore()
      .then((storeModule) => {
        if (!cancelled) {
          publishProjectName(storeModule.useVisualEditorStore.getState().projectName);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('[AppSidebar] load visual editor project metadata failed:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return useSyncExternalStore(
    subscribeProjectName,
    getProjectNameSnapshot,
    getProjectNameSnapshot,
  );
}

export function prepareVisualEditorProjectChatSidebar(projectId: string, open: boolean): void {
  preparedProjectChatSidebarState = { projectId, open };
}

export function consumeVisualEditorProjectChatSidebar(projectId: string): boolean | undefined {
  if (preparedProjectChatSidebarState?.projectId !== projectId) return undefined;

  const { open } = preparedProjectChatSidebarState;
  preparedProjectChatSidebarState = null;
  return open;
}

export async function syncVisualEditorProjectName(
  projectId: string,
  projectName: string,
): Promise<void> {
  const { useVisualEditorStore } = await loadVisualEditorStore();
  const store = useVisualEditorStore.getState();
  if (store.projectId === projectId) {
    store.setProjectName(projectName);
  }
}
